import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuthStore } from '@/stores/auth-store';
import { isNative } from '@/services/platform';
import { isPushOptedIn, registerPush, unregisterPush } from '@/services/push';

// 推送 refUrl 必须是站内相对路径,不允许 //、http、javascript:、..,
// 否则攻击者控制 push payload 后能让 victim navigate 到任意外站或触发深链 CSRF。
const SAFE_REF_URL = /^\/[a-zA-Z0-9/_\-?=&%.:]*$/;
function safeRefUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 1024) return null;
  if (raw.startsWith('//') || raw.includes('..')) return null;
  return SAFE_REF_URL.test(raw) ? raw : null;
}

/**
 * Wires push registration to the auth lifecycle.
 *
 * - On `authed` flip: re-register the token with backend so the latest
 *   userId-token mapping wins, AND attach foreground/background tap
 *   listeners. We only request OS permission if the user previously
 *   opted in via the Profile / Devices screen — never auto-prompt on
 *   first login (that's a worst-practice pattern that gets denied).
 *
 * - On `guest` flip: clear listeners and unregister.
 *
 * Native-only — desktop / web build short-circuits.
 */
export function usePushLifecycle() {
  const status = useAuthStore((s) => s.status);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;

    let tapListenerHandle: ReturnType<typeof PushNotifications.addListener> | null = null;
    let cancelled = false;

    void (async () => {
      if (status === 'authed' && (await isPushOptedIn())) {
        if (cancelled) return;
        void registerPush((notification) => {
          const refUrl = safeRefUrl(notification.data?.refUrl);
          toast(notification.title ?? '新通知', {
            description: notification.body ?? '',
            action: refUrl
              ? { label: '查看', onClick: () => navigate(refUrl) }
              : undefined,
          });
        });

        tapListenerHandle = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            const refUrl = safeRefUrl(action.notification.data?.refUrl);
            if (refUrl) {
              navigate(refUrl);
            }
          },
        );
      }

      if (status === 'guest') {
        void unregisterPush();
      }
    })();

    return () => {
      cancelled = true;
      if (tapListenerHandle) {
        void tapListenerHandle.then((l) => l.remove());
      }
    };
  }, [status, navigate]);
}
